/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import * as url from "helper/UrlHelper";
import { getData, postData } from "utils/Gateway";

const fieldBoxClass = "form-group mb-0 p-3 border rounded-8 shadow-sm bg-white h-100";
const inputClass = "form-control fs-sm shadow-none border border-gray-300";
const labelClass = "d-block fs-sm fw-semibold mb-2 text-dark";

// this modal is the Clorian-specific UI on top of the generic ticket-platform-integration API
const PLATFORM = "clorian";

const ClorianImportModal = ({ afterImportClose = () => {} }) => {
  const { t } = useTranslation();

  const [environment, setEnvironment] = useState("production");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [clientId, setClientId] = useState("");
  const [posId, setPosId] = useState("");
  const [hasCredentials, setHasCredentials] = useState(false);

  // which environments already have a saved connection for this platform + user,
  // e.g. [{ environment: "production", hascredentials: true }]
  const [savedEnvironments, setSavedEnvironments] = useState([]);

  const [singleDate, setSingleDate] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [isSavingConnection, setIsSavingConnection] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // each section reports its own result, independent of the others
  const [connectionResult, setConnectionResult] = useState(null); // { type, message }
  const [testResult, setTestResult] = useState(null); // { type, message }
  const [importResult, setImportResult] = useState(null); // { type, message }

  const getSavedEnvironments = async () => {
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_LIST_TICKET_PLATFORM_ENVIRONMENTS +
        `?platform=${PLATFORM}`;
      const response = await getData(requestUrl);

      if (response.status) {
        setSavedEnvironments(response.data || []);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    getSavedEnvironments();
  }, []);

  const getIntegration = async (forEnvironment) => {
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_TICKET_PLATFORM_INTEGRATION +
        `?platform=${PLATFORM}&environment=${forEnvironment}`;
      const response = await getData(requestUrl);

      if (response.status && response.data) {
        setUsername(response.data.username || "");
        setPassword(response.data.password || "");
        setClientId(response.data.clientid || "");
        setPosId(response.data.posid || "");
        setHasCredentials(Boolean(response.data.hascredentials));
      } else {
        setUsername("");
        setPassword("");
        setClientId("");
        setPosId("");
        setHasCredentials(false);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  // each environment has its own saved connection, so switching it reloads that connection
  useEffect(() => {
    getIntegration(environment);
  }, [environment]);

  //---------- section 1 : save connection ----------
  const saveConnection = async () => {
    try {
      setIsSavingConnection(true);
      setConnectionResult(null);
      setTestResult(null);

      let requestUrl = url.API_BASE_URL + url.API_SAVE_TICKET_PLATFORM_INTEGRATION;

      const payload = {
        platform: PLATFORM,
        environment,
        username,
        clientid: clientId,
        posid: posId,
      };

      if (password) {
        payload.password = password;
      }

      const response = await postData(requestUrl, payload);

      setIsSavingConnection(false);

      if (response.status) {
        setPassword(response.data?.password || password);
        setHasCredentials(Boolean(response.data?.hascredentials));
        setConnectionResult({ type: "success", message: t("Connection saved") });
        getSavedEnvironments();
      } else {
        setConnectionResult({ type: "error", message: response.message });
      }
    } catch (error) {
      setIsSavingConnection(false);
      setConnectionResult({ type: "error", message: error.message });
    }
  };

  // shared by the "Check Connection" button and by runImport's pre-flight check —
  // forces a fresh access-token request through our backend so we know Clorian
  // actually authenticates before anything else is attempted
  const verifyConnection = async () => {
    let requestUrl = url.API_BASE_URL + url.API_TEST_CLORIAN_CONNECTION;

    const response = await postData(requestUrl, { environment });

    if (response.status) {
      const expiresAt = response.data?.expiresat
        ? new Date(response.data.expiresat).toLocaleString()
        : "";
      return {
        ok: true,
        result: {
          type: "success",
          message: `${t("Access token obtained")}${
            expiresAt ? ` — ${t("expires")} ${expiresAt}` : ""
          }`,
        },
      };
    }

    return {
      ok: false,
      result: {
        type: "error",
        message: response.message,
        debug: response.debug,
      },
    };
  };

  //---------- section 2 : test connection (confirm an access token comes back) ----------
  const testConnection = async () => {
    try {
      setIsTestingConnection(true);
      setTestResult(null);

      const { result } = await verifyConnection();

      setIsTestingConnection(false);
      setTestResult(result);
    } catch (error) {
      setIsTestingConnection(false);
      setTestResult({ type: "error", message: error.message });
    }
  };

  //---------- section 3 : import purchases for a date / date range ----------
  const runImport = async () => {
    if (!startDate || (!singleDate && !endDate)) return;

    try {
      setIsImporting(true);
      setImportResult(null);

      // never sync data without first confirming the connection actually authenticates
      const { ok, result } = await verifyConnection();
      setTestResult(result);

      if (!ok) {
        setIsImporting(false);
        setImportResult({
          type: "error",
          message: `${t("Connection check failed, import aborted")}: ${result.message}`,
          debug: result.debug,
        });
        return;
      }

      let requestUrl = url.API_BASE_URL + url.API_IMPORT_CLORIAN_PURCHASES;

      const payload = {
        environment,
        startdate: startDate,
        enddate: singleDate ? startDate : endDate,
      };

      const response = await postData(requestUrl, payload);

      setIsImporting(false);

      if (response.status) {
        setImportResult({
          type: "success",
          message: `${t("Imported")}: ${response.data.imported} · ${t("Updated")}: ${response.data.updated} · ${t("Total")}: ${response.data.total}`,
        });
        afterImportClose();
      } else {
        setImportResult({
          type: "error",
          message: response.message,
          debug: response.debug,
        });
      }
    } catch (error) {
      setIsImporting(false);
      setImportResult({ type: "error", message: error.message });
    }
  };

  const renderResult = (result) => {
    if (!result) return null;
    return (
      <div className="mt-2">
        <div
          className={`fs-sm ${
            result.type === "error" ? "text-danger" : "text-success"
          }`}
        >
          {result.message}
        </div>
        {result.debug && (
          <pre
            className="fs-xs bg-light border rounded-8 p-2 mt-2 mb-0"
            style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}
          >
            {result.debug.method} {result.debug.url}
            {"\n"}
            {JSON.stringify(
              result.debug.params || result.debug.body || {},
              null,
              2,
            )}
          </pre>
        )}
      </div>
    );
  };

  const environmentLabel = environment === "qa" ? t("Testing") : t("Production");

  const isEnvironmentSaved = (envValue) =>
    savedEnvironments.some(
      (item) => item.environment === envValue && item.hascredentials,
    );

  return (
    <div
      className="modal fade"
      id="clorianImportModal"
      tabIndex="-1"
      aria-labelledby="clorianImportModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="clorianImportModalLabel">
              {t("Import from Clorian")}
            </h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <div className="modal-body">
            <div className="accordion mb-3" id="clorianAccordion">
              {/* ---------- section 1 : connection settings ---------- */}
              <div className="accordion-item">
                <h2 className="accordion-header" id="clorianConnectionHeading">
                  <button
                    className="accordion-button fw-semibold"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#clorianConnectionCollapse"
                    aria-expanded="true"
                    aria-controls="clorianConnectionCollapse"
                  >
                    1. {t("Add Connection")}
                  </button>
                </h2>
                <div
                  id="clorianConnectionCollapse"
                  className="accordion-collapse collapse show"
                  aria-labelledby="clorianConnectionHeading"
                >
                  <div className="accordion-body">
                    <div className="row g-3 mb-3">
                      <div className="col-md-4">
                        <div className={fieldBoxClass}>
                          <label className={labelClass}>{t("Environment")}</label>
                          <select
                            className={`form-select fs-sm shadow-none border border-gray-300`}
                            value={environment}
                            onChange={(e) => setEnvironment(e.target.value)}
                          >
                            <option value="production">
                              {t("Production")}
                              {isEnvironmentSaved("production")
                                ? ` (${t("saved")})`
                                : ""}
                            </option>
                            <option value="qa">
                              {t("Testing")}
                              {isEnvironmentSaved("qa") ? ` (${t("saved")})` : ""}
                            </option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className={fieldBoxClass}>
                          <label className={labelClass}>{t("Client ID")}</label>
                          <input
                            type="text"
                            className={inputClass}
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className={fieldBoxClass}>
                          <label className={labelClass}>
                            {t("Point of Sale ID")}
                          </label>
                          <input
                            type="text"
                            className={inputClass}
                            value={posId}
                            onChange={(e) => setPosId(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <div className={fieldBoxClass}>
                          <label className={labelClass}>{t("Username")}</label>
                          <input
                            type="text"
                            className={inputClass}
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className={fieldBoxClass}>
                          <label className={labelClass}>{t("Password")}</label>
                          <input
                            type="text"
                            className={inputClass}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="new-password"
                          />
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      disabled={isSavingConnection}
                      onClick={saveConnection}
                    >
                      {isSavingConnection ? t("Saving...") : t("Save Connection")}
                    </button>
                    {renderResult(connectionResult)}
                  </div>
                </div>
              </div>

              {/* ---------- section 2 : test connection ---------- */}
              <div className="accordion-item">
                <h2 className="accordion-header" id="clorianCheckHeading">
                  <button
                    className="accordion-button fw-semibold"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#clorianCheckCollapse"
                    aria-expanded="true"
                    aria-controls="clorianCheckCollapse"
                  >
                    2. {t("Check Connection")}
                  </button>
                </h2>
                <div
                  id="clorianCheckCollapse"
                  className="accordion-collapse collapse show"
                  aria-labelledby="clorianCheckHeading"
                >
                  <div className="accordion-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className={fieldBoxClass}>
                          <label className={labelClass}>
                            {t("Current connection")}
                          </label>
                          {hasCredentials ? (
                            <ul className="list-unstyled fs-sm mb-0">
                              <li className="mb-1">
                                <strong>{t("Environment")}:</strong>{" "}
                                {environmentLabel}
                              </li>
                              <li className="mb-1">
                                <strong>{t("Username")}:</strong> {username}
                              </li>
                              <li className="mb-1">
                                <strong>{t("Password")}:</strong> {password}
                              </li>
                              <li className="mb-1">
                                <strong>{t("Client ID")}:</strong> {clientId}
                              </li>
                              <li className="mb-0">
                                <strong>{t("Point of Sale ID")}:</strong> {posId}
                              </li>
                            </ul>
                          ) : (
                            <p className="fs-sm text-muted mb-0">
                              {t("Not configured yet")}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className={fieldBoxClass}>
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            disabled={isTestingConnection || !hasCredentials}
                            onClick={testConnection}
                          >
                            {isTestingConnection
                              ? t("Checking...")
                              : t("Check Connection")}
                          </button>
                          {renderResult(testResult)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ---------- section 3 : import purchases (always visible) ---------- */}
            <div className="p-3 border rounded-8 shadow-sm bg-white">
              <h6 className="fw-semibold mb-3">3. {t("Import Purchases")}</h6>
              <div className="form-check mb-3">
                <input
                  className="form-check-input me-2"
                  type="checkbox"
                  id="clorianSingleDate"
                  checked={singleDate}
                  onChange={(e) => setSingleDate(e.target.checked)}
                />
                <label
                  className="form-check-label fs-sm"
                  htmlFor="clorianSingleDate"
                >
                  {t("Single date")}
                </label>
              </div>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className={labelClass}>
                    {singleDate ? t("Date") : t("From date")}
                  </label>
                  <input
                    type="date"
                    className={inputClass}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                {!singleDate && (
                  <div className="col-md-4">
                    <label className={labelClass}>{t("To date")}</label>
                    <input
                      type="date"
                      className={inputClass}
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                )}
                <div className="col-md-4 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-primary w-100"
                    disabled={isImporting || !hasCredentials}
                    onClick={runImport}
                  >
                    {isImporting ? t("Importing...") : t("Run Import")}
                  </button>
                </div>
              </div>
              {renderResult(importResult)}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              {t("Close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClorianImportModal;
